import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Compass, Heart, Shield, Users, ArrowRight, BookOpen, RotateCw, ShieldCheck, LogIn, UserCheck, Sun, Sparkles, ShoppingBag, Music, Library, Stethoscope, HeartHandshake } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const careTypes = [
  {
    icon: Heart,
    title: "Home Care",
    description: "Non-medical help at home with bathing, dressing, meals, errands, and companionship.",
    color: "text-[#E76F51]",
    payment: "Private pay (typically $20–35/hour), long-term care insurance, VA benefits, and some Medicaid waiver programs. Medicare does NOT cover it.",
    criteria: "No medical requirement — available to anyone who needs help with daily activities. You choose the schedule, from a few hours a week to 24/7 live-in care."
  },
  {
    icon: Shield,
    title: "Home Health",
    description: "Skilled medical care at home — nursing, physical, occupational, and speech therapy.",
    color: "text-primary",
    payment: "Covered 100% by Medicare or Medicaid when criteria are met. Most private insurance also covers it.",
    criteria: "Requires a doctor's order, being 'homebound' (leaving home takes considerable effort), and a need for skilled nursing or therapy."
  },
  {
    icon: Users,
    title: "Palliative Care",
    description: "Palliative care helps you live better while living with a serious illness.",
    color: "text-accent",
    payment: "Covered by Medicare Part B, Medicaid, and most private insurance (regular copays may apply).",
    criteria: "An extra layer of support for symptoms, care decisions, emotional needs, and the needs of your family — at any stage of illness and alongside your regular medical treatment."
  },
  {
    icon: Compass,
    title: "Hospice Care",
    description: "Compassionate end-of-life care focused on comfort, dignity, and quality of life.",
    color: "text-secondary",
    payment: "Medicare hospice benefit covers it 100% — including medications, equipment, and support. Medicaid and most insurers cover it too.",
    criteria: "A physician certifies a life expectancy of 6 months or less, and care shifts from curing the illness to comfort."
  },
  {
    icon: UserCheck,
    title: "Senior Advisor",
    description: "A local guide who can connect you with senior-focused legal help, real estate and downsizing support, financial planning, and care placement when home care alone isn't enough.",
    color: "text-[#2AADA0]",
    payment: "Some advisors are free (paid by communities they refer to), others are private pay for fully independent guidance. Always ask up front how they're compensated — fee structures shape what gets recommended.",
    criteria: "Best for families dealing with bigger life transitions — when 24/7 care is needed, the home isn't safe anymore, you're sorting estate planning, Medicaid eligibility, or selling a longtime home. Not a starting point — an add-on when the situation gets complicated."
  },
  {
    icon: Sun,
    title: "Adult Day Care",
    description: "Daytime programs offering social activities, meals, supervision, and health services — so seniors stay engaged and caregivers get a much-needed break.",
    color: "text-[#F4A261]",
    payment: "Mostly private pay, but some Medicaid waiver programs, VA benefits, and long-term care insurance cover it. Sliding-scale fees are common.",
    criteria: "Seniors who benefit from socialization, supervision, or light medical oversight during the day — and family caregivers who need respite during work hours."
  },
  {
    icon: Stethoscope,
    title: "Home Physicians",
    description: "Physicians who make house calls — geriatric-focused primary care right at home for seniors who can't easily get to a doctor's office.",
    color: "text-primary",
    payment: "Medicare Part B covers most home-based physician visits. Many private insurers cover it too. Some concierge home-physician practices are private pay.",
    criteria: "Seniors with mobility limits, transportation barriers, or complex chronic conditions who benefit from a doctor coming to them."
  },
  {
    icon: HeartHandshake,
    title: "Community Partners",
    description: "Mission-driven organizations that surround your family with support — Meals on Wheels, local Departments on Aging, faith communities, caregiver support groups, disease-specific nonprofits, and volunteer services.",
    color: "text-secondary",
    payment: "Almost always free or donation-based. Community Partners are nonprofit and volunteer organizations who partner with Tyvena to help spread care — they don't charge families.",
    criteria: "Anyone looking for community, spiritual support, meals, transportation, education, or peer connection alongside professional care."
  }
];

export default function GetStartedPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoaded, setIsLoaded] = useState(false);
  const [flippedCards, setFlippedCards] = useState([]);

  const toggleFlip = (index) => {
    setFlippedCards((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const handleGetStarted = () => {
    navigate("/chat");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#EDF7F6] to-[#FAF9F6]">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between gap-4">
          <button
            type="button"
            data-testid="nav-home-brand"
            onClick={() => {
              // Already on /get-started — smoothly scroll to top of the
              // flip-tiles hero so the click still gives visible feedback.
              window.scrollTo({ top: 0, behavior: "smooth" });
              navigate("/get-started");
            }}
            className="flex items-center flex-shrink-0 group cursor-pointer"
            aria-label="Explore care types"
          >
            <img
              src="/tyvena-lockup-horizontal.png"
              alt="Tyvena"
              className="h-14 md:h-16 w-auto rounded-xl shadow-md group-hover:shadow-lg transition-shadow"
            />
          </button>
          <div className="flex items-center gap-2 md:gap-4 flex-wrap justify-end">
            <Button
              data-testid="nav-browse-providers-btn"
              onClick={() => navigate("/browse-providers")}
              variant="ghost"
              className="text-primary hover:bg-primary/5 font-body font-semibold text-sm md:text-base px-2 md:px-4"
            >
              Browse Providers
            </Button>
            <Button
              data-testid="nav-library-btn"
              onClick={() => navigate("/library")}
              variant="ghost"
              className="text-primary hover:bg-primary/5 font-body font-semibold text-sm md:text-base px-2 md:px-4"
            >
              <BookOpen className="w-4 h-4 mr-1 md:mr-2" />
              Library
            </Button>
            <Button
              data-testid="nav-history-btn"
              onClick={() => navigate("/history")}
              variant="ghost"
              className="text-primary hover:bg-primary/5 font-body font-semibold text-sm md:text-base px-2 md:px-4"
            >
              Conversations
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="nav-coming-soon-btn"
                  variant="ghost"
                  className="relative text-primary hover:bg-primary/5 font-body font-semibold text-sm md:text-base px-2 md:px-4"
                >
                  <Sparkles className="w-4 h-4 mr-1 md:mr-2 text-[#E07B54]" />
                  Extras
                  <span className="absolute -top-1 -right-1 flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E07B54] opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E07B54]"></span>
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72 bg-white border border-[#E2E8F0] shadow-xl rounded-xl p-2">
                <DropdownMenuLabel className="font-body text-xs uppercase tracking-wider text-text-secondary px-2 pt-1 pb-2">
                  Curated for the care journey
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="nav-shopping"
                  onClick={() => navigate("/shopping")}
                  className="cursor-pointer focus:bg-[#E07B54]/5 hover:bg-[#E07B54]/5 px-2 py-3 rounded-lg"
                >
                  <div className="flex items-start gap-3 w-full">
                    <div className="mt-0.5 w-9 h-9 rounded-lg bg-[#E07B54]/15 flex items-center justify-center flex-shrink-0">
                      <ShoppingBag className="w-4 h-4 text-[#E07B54]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-body font-semibold text-sm text-text-primary">Recommended Shopping</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#E07B54]/15 text-[#E07B54]">Preview</span>
                      </div>
                      <p className="text-xs text-text-secondary font-body leading-snug mt-0.5">
                        Curated products for the care journey — mobility aids, comfort, and trusted essentials.
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-music"
                  onClick={() => navigate("/music")}
                  className="cursor-pointer focus:bg-primary/5 hover:bg-primary/5 px-2 py-3 rounded-lg"
                >
                  <div className="flex items-start gap-3 w-full">
                    <div className="mt-0.5 w-9 h-9 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
                      <Music className="w-4 h-4 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-body font-semibold text-sm text-text-primary">Music for the Journey</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary">Preview</span>
                      </div>
                      <p className="text-xs text-text-secondary font-body leading-snug mt-0.5">
                        Thoughtfully curated playlists for memory care, calm moments, and shared time together.
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-books"
                  onClick={() => navigate("/books")}
                  className="cursor-pointer focus:bg-accent/5 hover:bg-accent/5 px-2 py-3 rounded-lg"
                >
                  <div className="flex items-start gap-3 w-full">
                    <div className="mt-0.5 w-9 h-9 rounded-lg bg-accent/12 flex items-center justify-center flex-shrink-0">
                      <Library className="w-4 h-4 text-accent" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-body font-semibold text-sm text-text-primary">Books & Resources</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/12 text-accent">Preview</span>
                      </div>
                      <p className="text-xs text-text-secondary font-body leading-snug mt-0.5">
                        Hand-picked books, guides, and free national resources for caregivers.
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="nav-inspiration"
                  onClick={() => navigate("/inspiration")}
                  className="cursor-pointer focus:bg-secondary/5 hover:bg-secondary/5 px-2 py-3 rounded-lg"
                >
                  <div className="flex items-start gap-3 w-full">
                    <div className="mt-0.5 w-9 h-9 rounded-lg bg-secondary/15 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4 text-secondary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-body font-semibold text-sm text-text-primary">Inspiration & Education</span>
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary/15 text-secondary">Coming soon</span>
                      </div>
                      <p className="text-xs text-text-secondary font-body leading-snug mt-0.5">
                        Daily &amp; weekly videos, our podcast, and short lessons.
                      </p>
                    </div>
                  </div>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {!user && (
              <Button
                data-testid="nav-signin-btn"
                onClick={() => navigate("/auth")}
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary/5 rounded-full font-body font-bold text-sm md:text-base px-3 md:px-5"
              >
                <LogIn className="w-4 h-4 mr-1 md:mr-2" />
                Sign In
              </Button>
            )}
            <Button
              data-testid="nav-portal-btn"
              onClick={() => navigate("/portal")}
              className="bg-primary text-white hover:bg-[#238C82] rounded-full font-body font-bold text-sm md:text-base px-3 md:px-5 shadow-md"
            >
              <ShieldCheck className="w-4 h-4 mr-1 md:mr-2" />
              My Portal
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${
          isLoaded ? 'animate-fade-in' : 'opacity-0'
        }`}>
          {/* Left Column - Text Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-secondary/20 rounded-full">
              <Compass className="w-4 h-4 text-secondary" />
              <span className="text-sm font-body font-semibold text-text-primary">
                Your Guide Through Care Options
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-text-primary leading-tight">
              Navigate Home Care with Confidence
            </h1>

            <p className="text-lg leading-relaxed text-text-secondary font-body">
              Like a lighthouse guiding ships through uncharted waters, Asher helps you understand and find the right care options for your loved ones. From home care to hospice, we're here to shed light on your journey.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="hero-get-started-btn"
                onClick={handleGetStarted}
                className="min-h-[48px] min-w-[48px] px-8 py-6 bg-primary text-white hover:bg-[#238C82] rounded-full font-bold text-lg shadow-md hover:shadow-lg transition-all duration-300 active:scale-95 font-body"
              >
                Talk to Asher
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                data-testid="hero-learn-more-btn"
                onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
                className="min-h-[48px] min-w-[48px] px-8 py-6 border-2 border-primary text-primary hover:bg-primary/10 rounded-full font-bold text-lg transition-all duration-300 bg-transparent font-body"
              >
                How It Works
              </Button>
            </div>
          </div>

          {/* Right Column - Branded Compass Hero */}
          <div className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl h-[500px] bg-gradient-to-br from-[#FAF9F6] via-[#F5EFE6] to-[#EDF7F6] flex items-center justify-center">
              {/* Ambient warm glow behind medallion */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(245,140,59,0.14),transparent_60%)]"></div>
              {/* Outer decorative ring */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[440px] h-[440px] rounded-full border-2 border-primary/10"></div>
              </div>
              {/* Inner subtle ring */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-[380px] h-[380px] rounded-full border border-primary/20"></div>
              </div>
              {/* Teal medallion — the compass sits on this coin */}
              <div className="relative w-[340px] h-[340px] rounded-full shadow-2xl overflow-hidden ring-8 ring-white/70">
                <img
                  src="/tyvena-mark-512.png"
                  alt="Tyvena compass mark"
                  className="w-full h-full object-cover"
                />
                {/* Soft top highlight for medallion depth */}
                <div className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-white/20 to-transparent pointer-events-none"></div>
              </div>
              {/* Corner tagline chip */}
              <div className="absolute bottom-6 left-6 right-6 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-md border border-primary/15 shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-[#E07B54] animate-pulse"></span>
                  <span className="text-xs font-body font-bold uppercase tracking-wider text-primary">A guide for the care journey</span>
                </div>
              </div>
            </div>
            {/* External soft glow decoration */}
            <div className="absolute -left-10 top-1/2 w-32 h-32 lighthouse-beam rounded-full blur-3xl pointer-events-none"></div>
          </div>
        </div>
      </section>

      {/* Dual-path CTA — pick the entry point that fits */}
      <section
        data-testid="dual-path-cta"
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-4 md:pb-8"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          <button
            data-testid="cta-talk-to-asher"
            onClick={handleGetStarted}
            className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary to-[#238C82] text-white p-6 md:p-8 text-left shadow-lg hover:shadow-2xl hover:-translate-y-0.5 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-white/15 backdrop-blur flex items-center justify-center">
                <Compass className="w-7 h-7 md:w-8 md:h-8 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-body font-bold uppercase tracking-wider text-white/80">Need help?</p>
                <p className="text-xl md:text-2xl font-heading font-bold mt-1">Talk to Asher</p>
              </div>
              <ArrowRight className="w-6 h-6 text-white/80 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>

          <button
            data-testid="cta-browse-providers"
            onClick={() => navigate("/browse-providers")}
            className="group relative overflow-hidden rounded-2xl bg-white border-2 border-primary/20 hover:border-primary p-6 md:p-8 text-left shadow-md hover:shadow-2xl hover:-translate-y-0.5 transition-all"
          >
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
                <Users className="w-7 h-7 md:w-8 md:h-8 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-body font-bold uppercase tracking-wider text-primary/70">Know what you need?</p>
                <p className="text-xl md:text-2xl font-heading font-bold text-text-primary mt-1">Search Providers</p>
              </div>
              <ArrowRight className="w-6 h-6 text-primary/70 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        </div>
      </section>

      {/* Care Types Section */}
      <section id="how-it-works" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
            Care Options We Help You Navigate
          </h2>
          <p className="text-lg text-text-secondary font-body max-w-2xl mx-auto">
            Asher guides you through different types of care, explaining each option clearly and helping you understand what's right for your situation.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {careTypes.map((item, index) => (
            <div
              key={index}
              data-testid={`care-type-${item.title.toLowerCase().replace(' ', '-')}`}
              className="flip-card h-[340px] md:h-[320px] cursor-pointer"
              onClick={() => toggleFlip(index)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleFlip(index); } }}
              aria-label={`${item.title} — tap to ${flippedCards.includes(index) ? 'see overview' : 'learn how it works'}`}
            >
              <div className={`flip-card-inner ${flippedCards.includes(index) ? 'flipped' : ''}`}>
                {/* Front */}
                <div className="flip-card-front bg-white rounded-xl border border-[#E2E8F0] shadow-sm hover:shadow-md transition-shadow duration-300 p-6 md:p-8 border-l-4 border-l-secondary flex flex-col">
                  <item.icon className={`w-12 h-12 ${item.color} mb-4`} strokeWidth={2} />
                  <h3 className="text-2xl font-heading font-semibold text-text-primary mb-3">
                    {item.title}
                  </h3>
                  <p className="text-base leading-relaxed text-text-secondary font-body">
                    {item.description}
                  </p>
                  <div className="mt-auto flex items-center gap-2 text-sm font-body font-semibold text-secondary pt-4">
                    <RotateCw className="w-4 h-4" />
                    Tap to learn how it's paid for & who qualifies
                  </div>
                </div>
                {/* Back */}
                <div
                  data-testid={`care-type-back-${item.title.toLowerCase().replace(' ', '-')}`}
                  className="flip-card-back bg-primary rounded-xl shadow-md p-6 md:p-7 flex flex-col text-white"
                >
                  <h3 className="text-xl font-heading font-semibold mb-3 flex items-center gap-2">
                    <item.icon className="w-5 h-5" strokeWidth={2} />
                    {item.title}
                  </h3>
                  <div className="space-y-3 text-sm leading-relaxed font-body">
                    <div>
                      <p className="font-bold text-secondary uppercase tracking-wide text-xs mb-0.5">How It's Paid For</p>
                      <p className="text-white/95">{item.payment}</p>
                    </div>
                    <div>
                      <p className="font-bold text-secondary uppercase tracking-wide text-xs mb-0.5">Who Qualifies</p>
                      <p className="text-white/95">{item.criteria}</p>
                    </div>
                  </div>
                  <div className="mt-auto flex items-center gap-2 text-xs font-body font-semibold text-white/70 pt-3">
                    <RotateCw className="w-3.5 h-3.5" />
                    Tap to flip back
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-text-primary mb-4">
              Simple Steps to Find the Right Care
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Share Your Situation",
                description: "Tell Asher about your loved one's needs and current circumstances."
              },
              {
                step: "2",
                title: "Get Personalized Guidance",
                description: "Asher asks thoughtful questions and explains your care options clearly."
              },
              {
                step: "3",
                title: "Connect with Providers",
                description: "Find qualified care companies in your area ready to help."
              }
            ].map((item, index) => (
              <div key={index} data-testid={`step-${item.step}`} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-secondary rounded-full text-text-primary font-heading font-bold text-2xl mb-6 shadow-lg">
                  {item.step}
                </div>
                <h3 className="text-xl font-heading font-semibold text-text-primary mb-3">
                  {item.title}
                </h3>
                <p className="text-base text-text-secondary font-body">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="bg-primary rounded-2xl p-12 text-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 right-10 w-64 h-64 bg-secondary rounded-full blur-3xl"></div>
            <div className="absolute bottom-10 left-10 w-64 h-64 bg-accent rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-6">
              Ready to Find the Right Care?
            </h2>
            <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto font-body">
              Let Asher guide you through your options with compassion and clarity.
            </p>
            <Button
              data-testid="cta-start-conversation-btn"
              onClick={handleGetStarted}
              className="min-h-[48px] min-w-[48px] px-8 py-6 bg-secondary text-text-primary hover:bg-[#E76F51] rounded-full font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 font-body"
            >
              Start Your Conversation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E2E8F0] py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/tyvena-lockup-horizontal.png"
                alt="Tyvena"
                className="h-9 w-auto rounded-lg shadow-md"
              />
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
              <Button
                data-testid="become-provider-btn"
                onClick={() => navigate("/provider-signup")}
                className="px-6 py-2 bg-secondary text-text-primary hover:bg-[#E76F51] rounded-full font-bold transition-all duration-300 font-body"
              >
                Request Provider Info
              </Button>
              <div className="flex items-center gap-4 text-sm font-body">
                <button
                  data-testid="footer-privacy-link"
                  onClick={() => navigate("/legal/privacy")}
                  className="text-text-secondary hover:text-primary transition-colors"
                >
                  Privacy
                </button>
                <span className="text-[#E2E8F0]">·</span>
                <button
                  data-testid="footer-hipaa-link"
                  onClick={() => navigate("/legal/hipaa-notice")}
                  className="text-text-secondary hover:text-primary transition-colors"
                >
                  HIPAA Notice
                </button>
              </div>
            </div>
          </div>
          <p className="text-xs text-text-secondary font-body text-center md:text-right mt-4">
            © 2026 Tyvena. A guide for the care journey.
          </p>
        </div>
      </footer>
    </div>
  );
}
